import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'

import {
  ExecuteCancelDialog,
  type ExecuteCancelDialogProps,
} from '~/components/elements/ExecuteCancelDialog'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { VStack } from '~/components/elements/VStack'
import { CustomerAttributeGroupSelector } from '~/components/features/CustomerAttributeGroupSelector'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useSurveyQueryCustomerAttributeGroups } from '~/hooks/useSurveyQuery'
import { exists } from '~/utils'

import { dialogInfoAtom } from '../atoms'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'
import { useCustomerAttributeGroup } from '../hooks/useCustomerAttributeGroup'
import { useMoveCustomerAttributeToGroupMutation } from '../hooks/useMoveCustomerAttributeToGroupMutation'

export const MoveToGroupDialog = () => {
  const dialogInfo = useAtomValue(dialogInfoAtom)
  if (dialogInfo?.dialogType !== 'moveToGroup') {
    throw new Error()
  }
  const { customerAttribute } = dialogInfo

  const currentCustomerAttributeGroup = useCustomerAttributeGroup()
  const allCustomerAttributeGroups = useSurveyQueryCustomerAttributeGroups()

  const availableCustomerAttributeGroups = React.useMemo(
    () =>
      allCustomerAttributeGroups.filter(
        (group) => group.id !== currentCustomerAttributeGroup?.id,
      ),
    [allCustomerAttributeGroups, currentCustomerAttributeGroup?.id],
  )

  const [selectedGroupId, setSelectedGroupId] = React.useState<string | null>(
    null,
  )

  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const logEvent = useLogEvent()
  const moveCustomerAttributeToGroupMutation =
    useMoveCustomerAttributeToGroupMutation({
      onSuccess: (data) => {
        // 移動先が別サーベイの場合や同名の属性が既に存在する場合などmoveCustomerAttributeToGroupはcustomerAttributeを返さない。移動できていないので記録しない。
        if (exists(data.moveCustomerAttributeToGroup?.customerAttribute)) {
          logEvent('Move customer attribute to group')
        }
        closeDialogWithRefresh()
      },
    })

  const handleExecute = React.useCallback<
    EH<ExecuteCancelDialogProps, 'onExecute'>
  >(() => {
    if (!selectedGroupId) {
      return
    }

    moveCustomerAttributeToGroupMutation.mutate({
      customerAttributeId: customerAttribute.id,
      customerAttributeGroupId: selectedGroupId,
    })
  }, [
    customerAttribute.id,
    moveCustomerAttributeToGroupMutation,
    selectedGroupId,
  ])

  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const { t } = useTranslation()

  return (
    <ExecuteCancelDialog
      disabledExecute={!exists(selectedGroupId)}
      executing={moveCustomerAttributeToGroupMutation.isPending}
      mode="update"
      title={t('change_definition_group')}
      onCancel={resetDialogInfo}
      onExecute={handleExecute}
    >
      <VStack spacing={4}>
        <div>
          <Trans
            components={{ strong: <strong /> }}
            i18nKey="change_definition_group_1"
            values={{ customSegmentName: customerAttribute.name }}
          />
        </div>

        <LabelingContainer label={t('move_to_definition_group')}>
          <CustomerAttributeGroupSelector
            customerAttributeGroups={availableCustomerAttributeGroups}
            unsetText={t('select_definition_group')}
            value={selectedGroupId}
            onChange={setSelectedGroupId}
          />
        </LabelingContainer>
      </VStack>
    </ExecuteCancelDialog>
  )
}
